import classNames from 'classnames';
import { useSetAtom } from 'jotai';
import React from 'react';
import { useQuery } from '@apollo/client';
import useAppSelector from '../../app/redux/useAppSelector';
import { gql } from '../../graphql/generated/gql';
import addTestId from '../../utils/addTestId';
import { controlBarInsightsStateAtom } from '../controlBar/controlBarMenuAtoms';
import { PostPolicyAction, usePostPolicyCheck } from '../postPolicyCheck';
import * as styles from './SharingDropdown.module.scss';
import SharingDropdownAccessList from './SharingDropdownAccessList';
import SharingDropdownAddSelect from './SharingDropdownAddSelect';
import SharingDropdownLink from './SharingDropdownLink';
import {
  useAddGroupToPost,
  useAddPostUser,
  useAddTopicToPost,
} from './postPermissionOperations';

const SHARING_DROPDOWN_QUERY = gql(/** @complexity 5 */ `
  query SharingDropdown($postId: ID!) {
    post(id: $postId) {
      id
      ...SharingDropdownAccessList_post
      ...SharingDropdownLink_post
      ...useAddPostUser_post
      ...usePostPolicyCheck_post
      ...SharingDropdownAddSelect_post
    }
  }
`);

interface SharingDropdownProps {
  close: () => void;
  postId: string;
  autoFocus?: boolean;
  title?: string;
}

const SharingDropdown = ({
  close,
  postId,
  autoFocus,
  title,
}: SharingDropdownProps) => {
  const currentUser = useAppSelector((state) => state.session.user);
  const postPolicyCheck = usePostPolicyCheck();

  const addTopic = useAddTopicToPost();
  const addUser = useAddPostUser();
  const addGroup = useAddGroupToPost();
  const setControlBarInsightsState = useSetAtom(controlBarInsightsStateAtom);

  const { data } = useQuery(SHARING_DROPDOWN_QUERY, { variables: { postId } });

  const post = data?.post;

  if (!post) {
    return null;
  }

  const canEdit = postPolicyCheck(PostPolicyAction.EditPost, post, currentUser);

  return (
    <div
      {...addTestId('sharingDropdown')}
      className={classNames(styles.container, {
        [styles.withTitle]: !!title,
      })}
    >
      {canEdit && (
        <div className={styles.addContainer}>
          <SharingDropdownAddSelect
            post={post}
            onAddTopic={(topicId: string) => addTopic(post.id, topicId)}
            onAddUser={(userId: string) => addUser(post, userId)}
            onAddGroup={(groupId: string) => addGroup(post.id, groupId)}
            autoFocus={autoFocus}
          />
        </div>
      )}
      <div
        className={styles.userContainer}
        {...addTestId('sharingDropdownUserContainer')}
      >
        <SharingDropdownAccessList
          post={post}
          onReplaceOwner={() => {
            setControlBarInsightsState({
              isOpen: true,
              submenu: 'owner',
            });
            close();
          }}
        />
      </div>
      <div className={styles.linkContainer}>
        <SharingDropdownLink post={post} onPricingModalOpen={close} />
      </div>
    </div>
  );
};

export default SharingDropdown;
